import { useState, useMemo, useEffect } from "react";
import { useTrackVisibility } from "@/hooks/use-track-visibility";
import { useColumnResize } from "@/hooks/use-column-resize";
import posthog from "@/lib/posthog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { TruncatedCell } from "@/components/ui/TruncatedCell";
import { ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDensity } from "@/contexts/DensityContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { listIntercompanyTags } from "@/services/intercompanyTag.service";
import IntercompanyModal from "./IntercompanyModal";
import { BankTransactionDto, BankTransactionPagination, IntercompanyTagDto } from "@blinkbooks/types";
import { format, parseISO } from "date-fns";

interface TransactionTableProps {
  transactions: BankTransactionDto[];
  onReconcileClick: (id: string) => void;
  allCategories: string[];
  onFetchCategories: () => Promise<void>;
  onCategoryChange: (transactionId: string, newCategory: string) => void;
  loading?: boolean;
  pagination: BankTransactionPagination;
  currentPage: number;
  onPageChange: (page: number) => void;
  openingBalance?: number;
  currency?: string;
}

// Date | Merchant | Category | Reference | Amount | Balance | Status
const TRANSACTION_COL_WIDTHS = [100, 170, 130, 110, 120, 120, 90];

const TransactionTable = ({
  transactions,
  onReconcileClick,
  allCategories,
  onFetchCategories,
  onCategoryChange,
  loading = false,
  pagination,
  currentPage,
  onPageChange,
  openingBalance,
  currency = "USD",
}: TransactionTableProps) => {
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categorySearch, setCategorySearch] = useState<string>("");
  const [intercompanyTagByTxnId, setIntercompanyTagByTxnId] = useState<Map<string, IntercompanyTagDto>>(new Map());
  const [intercompanyModalTxnId, setIntercompanyModalTxnId] = useState<string | null>(null);
  const { cellPad, rowH } = useDensity();
  const { activeViewId } = useOrganization();
  const visibilityRef = useTrackVisibility("banking_transaction_table");
  const { widths, totalWidth, startResize } = useColumnResize(TRANSACTION_COL_WIDTHS);

  // Intercompany tags are a single-company action — the API rejects every
  // /intercompany-tags call while a multi-entity View is active. Mirror the P&L
  // screen and hide the whole affordance in that context.
  const canTag = !activeViewId;

  useEffect(() => {
    if (!canTag) {
      setIntercompanyTagByTxnId(new Map());
      return;
    }
    listIntercompanyTags()
      .then((tags) => {
        const map = new Map<string, IntercompanyTagDto>();
        tags.filter((t) => t.entityType === "bank_transaction").forEach((t) => map.set(t.entityId, t));
        setIntercompanyTagByTxnId(map);
      })
      .catch(() => {
        // Best-effort — the intercompany indicator just starts untagged if this fails to load.
      });
  }, [canTag]);

  const handleIntercompanySaved = (transactionId: string, tag: IntercompanyTagDto | null) => {
    setIntercompanyTagByTxnId((prev) => {
      const next = new Map(prev);
      if (tag) next.set(transactionId, tag);
      else next.delete(transactionId);
      return next;
    });
  };

  const intercompanyModalTxn = intercompanyModalTxnId
    ? transactions.find((t) => String(t.id) === intercompanyModalTxnId)
    : undefined;

  const handlePageChange = (page: number) => {
    posthog.capture("table_paginated", { table: "banking_transactions", page });
    onPageChange(page);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDate = (dateStr: string) => {
    try { return format(parseISO(dateStr), "MMM d"); }
    catch { return dateStr; }
  };

  const parseCategory = (category: string | undefined) => {
    if (!category) return { parent: null, leaf: null };
    const parts = category.split(":");
    return {
      leaf: parts[parts.length - 1]?.trim(),
      parent: parts.length > 1 ? parts.slice(0, -1).join(":").trim() : null,
    };
  };

  const getCategoryChipClass = (leaf: string | null): string => {
    if (!leaf) return "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400";
    const l = leaf.toLowerCase();
    if (/software|subscript|saas|tech|cloud|app|tool/.test(l))
      return "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400";
    if (/advertis|market|social|media|seo|pr$/.test(l))
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400";
    if (/travel|meal|food|restaurant|entertain|hotel|flight|coffee/.test(l))
      return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400";
    if (/rent|storage|lease|facilit|propert|utilit|office/.test(l))
      return "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400";
    return "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400";
  };

  const filteredCategories = useMemo(() => {
    if (!categorySearch) return allCategories;
    return allCategories.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()));
  }, [allCategories, categorySearch]);

  const handleCategoryClick = async (txId: string, currentCategory: string) => {
    setEditingCategoryId(txId);
    setCategorySearch(currentCategory);
    await onFetchCategories();
  };

  const handleCategorySelect = (txId: string, category: string) => {
    posthog.capture("transaction_category_changed", { transaction_id: txId, category });
    onCategoryChange(txId, category);
    setEditingCategoryId(null);
    setCategorySearch("");
  };

  // Compute running balance: derive in chronological (ASC) order, display in current (DESC) order
  const rowsWithBalance = useMemo(() => {
    if (openingBalance == null) return transactions.map(t => ({ ...t, runningBalance: null as number | null }));
    // Transactions are DESC; reverse to compute ASC running balance
    const asc = [...transactions].reverse();
    let balance = Number(openingBalance);
    const balances: number[] = asc.map(t => {
      balance += t.direction === "IN" ? Number(t.amount) : -Number(t.amount);
      return balance;
    });
    // Re-attach in DESC order
    const descBalances = [...balances].reverse();
    return transactions.map((t, i) => ({ ...t, runningBalance: descBalances[i] ?? null }));
  }, [transactions, openingBalance]);

  return (
    <div ref={visibilityRef} className="space-y-3">
      <div className="rounded-b-lg border border-border/60 overflow-x-auto bg-card">
        <Table className="table-fixed w-full" style={{ minWidth: totalWidth }}>
          <TableHeader>
            <TableRow>
              <TableHead className="relative group overflow-hidden" style={{ width: widths[0] }}>
                Date
                <ResizeHandle onMouseDown={e => startResize(0, e)} className="opacity-0 group-hover:opacity-100" />
              </TableHead>
              <TableHead className="relative group overflow-hidden" style={{ width: widths[1] }}>
                Merchant
                <ResizeHandle onMouseDown={e => startResize(1, e)} className="opacity-0 group-hover:opacity-100" />
              </TableHead>
              <TableHead className="relative group overflow-hidden" style={{ width: widths[2] }}>
                Category
                <ResizeHandle onMouseDown={e => startResize(2, e)} className="opacity-0 group-hover:opacity-100" />
              </TableHead>
              <TableHead className="relative group overflow-hidden" style={{ width: widths[3] }}>
                Reference
                <ResizeHandle onMouseDown={e => startResize(3, e)} className="opacity-0 group-hover:opacity-100" />
              </TableHead>
              <TableHead className="relative group overflow-hidden text-right" style={{ width: widths[4] }}>
                Amount
                <ResizeHandle onMouseDown={e => startResize(4, e)} className="opacity-0 group-hover:opacity-100" />
              </TableHead>
              <TableHead className="relative group overflow-hidden text-right" style={{ width: widths[5] }}>
                Balance
                <ResizeHandle onMouseDown={e => startResize(5, e)} className="opacity-0 group-hover:opacity-100" />
              </TableHead>
              <TableHead className="overflow-hidden" style={{ width: widths[6] }}>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow key="loading">
                <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Loading transactions...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow key="empty">
                <TableCell colSpan={7} className="text-center text-muted-foreground py-10 text-sm">
                  No transactions match the selected filters
                </TableCell>
              </TableRow>
            ) : (
              rowsWithBalance.map((transaction) => {
                const txId = String(transaction.id);
                const isUnreconciled = transaction.status !== "MATCHED";
                const isOut = transaction.direction === "OUT";

                return (
                  <TableRow
                    key={txId}
                    className={cn(
                      rowH,
                      "transition-colors",
                      isUnreconciled && "bg-amber-50/50 dark:bg-amber-950/15"
                    )}
                    style={isUnreconciled ? { boxShadow: "inset 3px 0 0 rgb(251 191 36 / 0.9)" } : undefined}
                  >
                    {/* Date */}
                    <TableCell className={cn(cellPad, "text-sm text-muted-foreground whitespace-nowrap overflow-hidden")}>
                      {formatDate(transaction.transactionDate)}
                    </TableCell>

                    {/* Merchant */}
                    <TableCell className={cn(cellPad, "overflow-hidden")}>
                      <TruncatedCell content={transaction.merchantName} className="text-sm font-medium" />
                    </TableCell>

                    {/* Category */}
                    <TableCell className={cellPad}>
                      <Popover
                        open={editingCategoryId === txId}
                        onOpenChange={open => { if (!open) { setEditingCategoryId(null); setCategorySearch(""); } }}
                      >
                        <PopoverTrigger asChild>
                          <button
                            className="flex flex-col items-start gap-0.5 w-full text-left hover:opacity-80 transition-opacity"
                            onClick={() => handleCategoryClick(txId, transaction.category || "")}
                          >
                            {transaction.category ? (
                              <>
                                {parseCategory(transaction.category).parent && (
                                  <span className="text-[10px] text-muted-foreground leading-tight">
                                    {parseCategory(transaction.category).parent}
                                  </span>
                                )}
                                <span className={cn(
                                  "inline-flex items-center text-[11px] font-medium rounded-full px-2 py-0.5 w-fit",
                                  getCategoryChipClass(parseCategory(transaction.category).leaf ?? null)
                                )}>
                                  {parseCategory(transaction.category).leaf}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground/50">—</span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2" align="start">
                          <div className="space-y-2">
                            <Input
                              placeholder="Search categories..."
                              value={categorySearch}
                              onChange={e => setCategorySearch(e.target.value)}
                              autoFocus
                            />
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {filteredCategories.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-2">No categories found</p>
                              ) : (
                                filteredCategories.map((cat, idx) => (
                                  <Button
                                    key={`${cat}-${idx}`}
                                    variant="ghost"
                                    className="w-full justify-start h-8 px-2 text-sm"
                                    onClick={() => handleCategorySelect(txId, cat)}
                                  >
                                    {cat}
                                  </Button>
                                ))
                              )}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TableCell>

                    {/* Reference */}
                    <TableCell className={cn(cellPad, "overflow-hidden")}>
                      <TruncatedCell content={transaction.description} className="text-xs text-muted-foreground" />
                    </TableCell>

                    {/* Amount — colored by direction */}
                    <TableCell
                      className={cn(cellPad, "text-right font-medium tabular-nums text-sm")}
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      <span className={isOut ? "text-rose-600" : "text-emerald-700 dark:text-emerald-400"}>
                        {isOut ? "−" : "+"}{formatCurrency(Math.abs(Number(transaction.amount)))}
                      </span>
                    </TableCell>

                    {/* Running balance */}
                    <TableCell
                      className={cn(cellPad, "text-right text-sm tabular-nums text-muted-foreground")}
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {transaction.runningBalance != null ? (
                        <span className={transaction.runningBalance < 0 ? "text-rose-500" : ""}>
                          {formatCurrency(transaction.runningBalance)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/30">—</span>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell className={cn(cellPad, "flex items-center gap-1.5")}>
                      {transaction.status === "MATCHED" ? (
                        <span className="inline-flex items-center text-[11px] font-medium rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                          Matched
                        </span>
                      ) : (
                        <button
                          onClick={() => onReconcileClick(txId)}
                          title="Click to reconcile"
                          className="inline-flex items-center text-[11px] font-medium rounded-full px-2.5 py-1 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors cursor-pointer"
                        >
                          Review
                        </button>
                      )}
                      {canTag && (
                        <button
                          onClick={() => setIntercompanyModalTxnId(txId)}
                          title={
                            intercompanyTagByTxnId.has(txId)
                              ? intercompanyTagByTxnId.get(txId)!.status === "assigned"
                                ? "Assigned to a company — click to change"
                                : "Ignored as intercompany — click to change"
                              : "Mark as intercompany — this transaction also exists in another company"
                          }
                          className={cn(
                            "inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors shrink-0",
                            intercompanyTagByTxnId.has(txId)
                              ? intercompanyTagByTxnId.get(txId)!.status === "assigned"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
                                : "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400"
                              : "text-muted-foreground/30 hover:text-muted-foreground hover:bg-muted/50"
                          )}
                        >
                          <Building2 className="w-3 h-3" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-sm text-muted-foreground">
            {((currentPage - 1) * pagination.limit) + 1}–{Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} transactions
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading} className="gap-1">
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let p: number;
                if (pagination.totalPages <= 5) p = i + 1;
                else if (currentPage <= 3) p = i + 1;
                else if (currentPage >= pagination.totalPages - 2) p = pagination.totalPages - 4 + i;
                else p = currentPage - 2 + i;
                return (
                  <Button key={p} variant={p === currentPage ? "default" : "outline"} size="sm" onClick={() => handlePageChange(p)} disabled={loading} className="w-8 h-8 p-0">{p}</Button>
                );
              })}
            </div>
            <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pagination.totalPages || loading} className="gap-1">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {canTag && intercompanyModalTxn && (
        <IntercompanyModal
          open={intercompanyModalTxnId !== null}
          onClose={() => setIntercompanyModalTxnId(null)}
          entityId={String(intercompanyModalTxn.id)}
          entityOrganizationId={intercompanyModalTxn.organizationId}
          vendor={intercompanyModalTxn.merchantName}
          amount={Number(intercompanyModalTxn.amount)}
          currency={currency}
          date={intercompanyModalTxn.transactionDate}
          existingTag={intercompanyTagByTxnId.get(String(intercompanyModalTxn.id)) ?? null}
          onSaved={(tag) => handleIntercompanySaved(String(intercompanyModalTxn.id), tag)}
        />
      )}
    </div>
  );
};

export default TransactionTable;
